class LreCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.track = this.querySelector('[data-track]');
    this.autoplayToggle = this.querySelector('[data-autoplay-toggle]');
    this.interval = Math.max(Number(this.dataset.interval) || 5000, 3000);
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.userPaused = false;
    this.hoverPaused = false;
    this.focusPaused = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.handleClick = (event) => {
      const control = event.target.closest('[data-direction]');
      if (control && this.track) {
        this.userPaused = true;
        this.updateAutoplayControl();
        this.stopAutoplay();
        this.move(control.dataset.direction);
        return;
      }
      if (event.target.closest('[data-autoplay-toggle]')) this.toggleAutoplay();
    };
    this.handlePointerEnter = () => { this.hoverPaused = true; this.stopAutoplay(); };
    this.handlePointerLeave = () => { this.hoverPaused = false; this.startAutoplay(); };
    this.handleFocusIn = () => { this.focusPaused = true; this.stopAutoplay(); };
    this.handleFocusOut = (event) => {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.focusPaused = false;
      this.startAutoplay();
    };
    this.handleInteraction = () => {
      this.userPaused = true;
      this.updateAutoplayControl();
      this.stopAutoplay();
    };
    this.handleVisibility = () => document.hidden ? this.stopAutoplay() : this.startAutoplay();
    this.handleMotionChange = () => this.reducedMotion.matches ? this.stopAutoplay() : this.startAutoplay();

    this.addEventListener('click', this.handleClick);
    this.addEventListener('mouseenter', this.handlePointerEnter);
    this.addEventListener('mouseleave', this.handlePointerLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    this.track?.addEventListener('touchstart', this.handleInteraction, { passive: true });
    this.track?.addEventListener('wheel', this.handleInteraction, { passive: true });
    this.track?.addEventListener('keydown', this.handleInteraction);
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.reducedMotion.addEventListener('change', this.handleMotionChange);
    this.updateAutoplayControl();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('mouseenter', this.handlePointerEnter);
    this.removeEventListener('mouseleave', this.handlePointerLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.track?.removeEventListener('touchstart', this.handleInteraction);
    this.track?.removeEventListener('wheel', this.handleInteraction);
    this.track?.removeEventListener('keydown', this.handleInteraction);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.reducedMotion?.removeEventListener('change', this.handleMotionChange);
  }

  move(direction = 'next') {
    if (!this.track) return;
    const slide = this.track.querySelector('.lre-card-slide');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const distance = (slide?.getBoundingClientRect().width || this.track.clientWidth) + gap;
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - distance * 0.5;
    const atStart = this.track.scrollLeft <= distance * 0.5;
    if (direction === 'next' && atEnd) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (direction === 'previous' && atStart) {
      this.track.scrollTo({ left: this.track.scrollWidth, behavior: 'smooth' });
    } else {
      this.track.scrollBy({ left: direction === 'next' ? distance : -distance, behavior: 'smooth' });
    }
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.userPaused || this.hoverPaused || this.focusPaused || document.hidden || this.reducedMotion.matches || this.timer) return;
    this.timer = window.setInterval(() => this.move('next'), this.interval);
  }

  stopAutoplay() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  toggleAutoplay() {
    this.userPaused = !this.userPaused;
    this.updateAutoplayControl();
    if (this.userPaused) this.stopAutoplay();
    else this.startAutoplay();
  }

  updateAutoplayControl() {
    if (!this.autoplayToggle) return;
    this.autoplayToggle.hidden = this.reducedMotion.matches;
    this.autoplayToggle.setAttribute('aria-pressed', String(this.userPaused));
    this.autoplayToggle.setAttribute('aria-label', this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel);
    this.autoplayToggle.querySelector('[data-autoplay-icon]').textContent = this.userPaused ? '▶' : 'Ⅱ';
  }
}

if (!customElements.get('lre-carousel')) customElements.define('lre-carousel', LreCarousel);
